const amountFormattingOptions = {
  style: `currency`,
  currency: `EUR`,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
} as const;

// https://stackoverflow.com/a/40724354
const SI_SYMBOL = [``, `k`, `M`, `G`, `T`, `P`, `E`] as const;
type SiSymbol = typeof SI_SYMBOL[number];
interface AbbreviateNumber {
  count: number
  symbol: SiSymbol
  intlConfig: Intl.NumberFormatOptions
}
function abbreviateNumber(number: number): AbbreviateNumber {
  const tier = (Math.log10(Math.abs(number)) / 3) | 0;
  if (tier === 0) {
    return { count: number, symbol: ``, intlConfig: { minimumFractionDigits: 0, maximumFractionDigits: 0 } };
  }
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = number / scale;
  const digits = suffix === `k` ? 1 : 2;
  return {
    count: scaled,
    symbol: suffix ?? ``,
    intlConfig: { minimumFractionDigits: digits, maximumFractionDigits: digits },
  };
}

export default function useIadFormat() {
  const { t, n } = useI18n();
  // NOTE: “mandatory” to avoid collusion with “required” props
  /**
   * Return a string with a `*` if needed
   */
  function mandatory(text: MaybeRef<string | number | null | undefined>, mandatory?: MaybeRef<boolean>) {
    const _text = toValue(text);
    const _required = toValue(mandatory);
    if (typeof _text !== `string`) return ``;
    if (!_text) return ``;
    if (_required !== true) return _text;
    return `${_text}*`;
  }

  /**
   * Shortcut to ease the writing of text always mandatory
   */
  function m(text: MaybeRef<string>, replacements?: MaybeRef<Record<string, unknown>>) {
    if (!replacements) return mandatory(t(toValue(text)), true);
    return mandatory(t(toValue(text), toValue(replacements)), true);
  }

  function surface(value: MaybeRef<number | null | undefined>) {
    const surfaceValue = toValue(value);
    if (typeof surfaceValue !== `number`) return ``;
    const formatted = n(surfaceValue, { minimumFractionDigits: 0 });
    return `${formatted} m²`;
  }

  function perSurface(value: MaybeRef<number | null | undefined>) {
    const surfaceValue = toValue(value);
    if (typeof surfaceValue !== `number`) return ``;
    return `${amount(value)} / m²`;
  }

  function amount(value: MaybeRef<number | null | undefined>) {
    const amountValue = toValue(value);
    if (typeof amountValue !== `number`) return ``;
    return n(amountValue, amountFormattingOptions);
  }

  function largeNumber(value: MaybeRef<number | null | undefined>) {
    const numberValue = toValue(value);
    if (typeof numberValue !== `number`) return ``;
    const { count, symbol, intlConfig } = abbreviateNumber(numberValue);
    return n(count, intlConfig) + symbol;
  }

  function largeAmount(value: MaybeRef<number | null | undefined>) {
    const amountValue = toValue(value);
    if (typeof amountValue !== `number`) return ``;
    const currencyPlaceholder = amount(666).replace(/666/, `AMOUNT_PLACEHOLDER`);
    const formattedLargeAmount = largeNumber(value);
    return currencyPlaceholder.replace(/AMOUNT_PLACEHOLDER/, formattedLargeAmount);
  }

  function percentage(value: MaybeRef<number | null | undefined>) {
    const percentageValue = toValue(value);
    if (typeof percentageValue !== `number`) return ``;
    return n(percentageValue, { style: `percent`, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return {
    amount,
    m,
    mandatory,
    surface,
    largeNumber,
    largeAmount,
    percentage,
    perSurface,
  };
}
